import { Component, ElementRef, ViewChild } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { WhatsappMessageService } from '../../comunications/_services/whatsapp-messages.service';
import { WhatsappMessage, WhatsappMessageDirection } from '../../comunications/_types/whatsapp-message';
import { IParamsCollection } from 'src/app/core/jsonapi/interfaces/params-collection';
import { firstValueFrom, last } from 'rxjs';
import { FilterBuilder } from 'src/app/core/jsonapi/filters-builder';
import { Filter, FilterOperation, OrFilter } from 'src/app/core/jsonapi/filter';
import { LocalStorageService } from 'angular-web-storage';
import { User } from '../../users/_types/user';
import { CasesService } from '../../cases/_services/cases.service';
import { Case } from '../../cases/_types/case';
import { IParamsResource } from 'src/app/core/jsonapi/interfaces/params-resource';
import moment from 'moment';
import { TemplatesService } from '../../templates/_services/templates.service';
import { Template } from '../../templates/_types/template';
import { MessageService } from 'primeng/api';
import { environment } from 'src/environment/environment';

@Component({
    selector: 'app-whatsapp-chat.component',
    templateUrl: './whatsapp-chat.component.html',
    styleUrls: ['./whatsapp-chat.component.scss'],
    standalone: false
})
export class WhatsAppChatComponent {
  
  @ViewChild('chatMessages') public chatMessages!:ElementRef;

  public messages: WhatsappMessage[] = [];
  public user!:User;
  public case!:Case;
  public destinationName: string = '';
  public destinationPhoneNumber: string = '';
  public message:string = '';
  public template_id:string = '';
  private loadMessagesTimeout:any;
  //la primera carga si baja hasta el ultimo mensaje; a partir de ahi solo si el usuario ya estaba
  //abajo, para no arrastrarle la vista mientras lee mensajes anteriores
  private firstMessagesLoad:boolean = true;
  //la ventana de atencion de WhatsApp la abre el cliente cuando nos escribe, y cada mensaje entrante
  //suyo la renueva. Nuestras plantillas NO la abren: son justo el unico mensaje permitido fuera de
  //ella. Fuera de la ventana, Twilio rechaza los mensajes libres con el error 63016.
  private static readonly SESSION_WINDOW_HOURS = 24;
  public isSessionOpen:boolean = false;
  public sessionExpiresAt:Date|null = null;
  public sessionRemaining:string = '';       //"3 h 12 min"
  public sessionEndingSoon:boolean = false;  //queda menos de una hora
  private sessionWindowInterval:any;
  public templates:Template[] = [];
  public templateSearch:string = '';
  public displayTemplateDialog:boolean = false;
  public imagesUrl: string = environment.api_gw + 'common/files/';  

  constructor(
    private config: DynamicDialogConfig,
    private dynamicDialogRef: DynamicDialogRef,
    private whatsappMessageService: WhatsappMessageService,
    private storage:LocalStorageService,
    private caseService: CasesService,
    private templatesService: TemplatesService,
    private messageService: MessageService
  ) {
    this.user = this.storage.get('user');
    this.destinationPhoneNumber = this.config.data.phoneNumber;
    this.destinationName = this.config.data.name || 'Desconocido';
  }

  ngOnInit() {
    this.loadCase();
    this.loadMessages();
    this.loadTemplates();

    //marcamos como leidos los entrantes de este telefono. Va aqui y no en el sidebar para que valga
    //para las cinco vias por las que se abre el chat: expediente, servicio, dashboard, procesador y
    //el panel de chats. Es best-effort: si falla, no molestamos al usuario con un error.
    this.whatsappMessageService.MarkRead(this.destinationPhoneNumber).subscribe({
      next: () => this.storage.remove('chatsNotReaded'),
      error: (error: any) => console.error('Error marcando los whatsapp como leidos', error)
    });

    //la ventana caduca sola, sin que llegue ningun mensaje nuevo: la reevaluamos cada minuto para no
    //depender del polling de mensajes y que el input se bloquee al vencer.
    this.sessionWindowInterval = setInterval(() => this.updateSessionWindow(), 60000);
  }

  ngOnDestroy() {
    if(this.loadMessagesTimeout) clearTimeout(this.loadMessagesTimeout);
    if(this.sessionWindowInterval) clearInterval(this.sessionWindowInterval);
  }

  async loadCase() {
    if(this.config.data.caseId) {
      let params:IParamsResource = {
        include: ['client', 'client.address']
      };
      this.case = await firstValueFrom(this.caseService.get(this.config.data.caseId, params));
    }
  }

  async loadTemplates(){
    let filters = {
      templateType: new Filter(FilterOperation.equals, 'templateType', '', 'Whatsapp'),
      templateDestinationType: new OrFilter([
        new Filter(FilterOperation.equals, 'templateDestinationType', '', null, true)
      ]),        
    };          

    let params: IParamsCollection = {
      sort: ['name'],
      filter: FilterBuilder.fromObject(filters).build()
    }
    //cargamos las plantillas 
    let response = await firstValueFrom(this.templatesService.all(params));
    this.templates = response.data;
  }

  async loadMessages(fromDate?:Date) {
    let lastDate:Date = new Date();
    let phone = this.config.data.phoneNumber.replace('+','').replace(' ',''); //quitamos espacios y +
    let phone1 = phone;
    let phone2 = phone.startsWith('+') ? phone: '+34' + phone; //numero con prefijo
    let filters = {
      phoneNumber: new OrFilter([
        new Filter(FilterOperation.equals, 'phoneNumber', '', phone1),
        new Filter(FilterOperation.equals, 'phoneNumber', '', phone2)
      ]),
      createdAt: new Filter(FilterOperation.greaterThan, 'createdAt', '')
    };
    if(fromDate!=null){
      //let nextFromDate = moment(fromDate).set('milliseconds', 999).toDate();
      //obtenemos los mensajes de los ultimos 10 minutos para actualizar estados
      let nextFromDate = moment(fromDate).add(-10, 'minute').toDate();
      filters.createdAt.value = nextFromDate;
    }
    let params:IParamsCollection = {
      page: {
        size: 1000,
        number: 1
      },
      sort: ['createdAt'],
      include: ['createdByUser'],
      filter: FilterBuilder.fromObject(filters).build(),
    };
    let response = await firstValueFrom(this.whatsappMessageService.all(params, true));

    //miramos si el usuario esta al final ANTES de añadir nada: es lo que decide si bajamos la vista
    let wasAtBottom = this.isChatAtBottom();

    //añadimos los mensajes al array y calculamos ultima fecha
    let newMessages = 0;
    response.data.forEach(message => {
      let existsMessage = this.messages.find(m => m.id == message.id);
      message.content = this.prepareContent(message.content);
      if(existsMessage){
        existsMessage = Object.assign(existsMessage, message);
      }else{
        this.messages.push(message);
        newMessages++;
      }
      lastDate = message.createdAt;
    });

    //el polling recarga cada 5 segundos: si bajasemos siempre, no habria forma de leer el historial.
    //Solo bajamos en la primera carga y cuando llegan mensajes nuevos con el usuario ya al final.
    if(this.firstMessagesLoad || (newMessages > 0 && wasAtBottom)) this.scrollChatMessages();
    this.firstMessagesLoad = false;

    //reevaluamos la ventana de 24 h con los mensajes que acabamos de cargar
    this.updateSessionWindow();

    //siguiente carga a partir del ultimo mensaje
    this.loadMessagesTimeout = setTimeout(() => {
      this.loadMessages(lastDate);
    }, 5000);
  }

  //recalcula el estado de la ventana a partir del ultimo mensaje entrante del cliente. Cada entrante
  //nuevo la renueva, asi que solo hace falta mirar el mas reciente.
  updateSessionWindow() {
    let lastInbound = this.messages
      .filter(m => m.direction == WhatsappMessageDirection.Inbound)
      .sort((a, b) => moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf())[0];

    if (lastInbound == null) {
      this.sessionExpiresAt = null;
      this.isSessionOpen = false;
      this.sessionRemaining = '';
      this.sessionEndingSoon = false;
      return;
    }

    this.sessionExpiresAt = moment(lastInbound.createdAt)
      .add(WhatsAppChatComponent.SESSION_WINDOW_HOURS, 'hours').toDate();
    let minutesLeft = moment(this.sessionExpiresAt).diff(moment(), 'minutes');
    this.isSessionOpen = minutesLeft > 0;
    this.sessionEndingSoon = this.isSessionOpen && minutesLeft < 60;
    this.sessionRemaining = this.isSessionOpen ? this.formatRemaining(minutesLeft) : '';
  }

  //"3 h 12 min" cuando queda mas de una hora, "45 min" cuando no
  private formatRemaining(minutes: number): string {
    let hours = Math.floor(minutes / 60);
    let mins = minutes % 60;
    return hours > 0 ? `${hours} h ${mins} min` : `${mins} min`;
  }

  prepareContent(content:string):string{
    //reemplazamos los saltos de linea por <br>
    content = content.replace(/\n/g, '<br>');

    //reemplazamos los [IMAGE:guid] por la imagen correspondiente
    let regex = /\[IMAGE:(.*?)\]/g;
    let matches = content.matchAll(regex);
    for (const match of matches) {
      let fileId = match[1];
      let imageHtml = '<br><img src="' + this.imagesUrl + fileId + '/thumbnail/medium?resizeMode=max" /><br><a href="' + this.imagesUrl + fileId + '/download" target="_blank">Descargar</a>';
      //console.log(imageHtml);
      content = content.replace(match[0], imageHtml);
    }

    //reemplazamos los [FILE:guid] por la imagen correspondiente
    regex = /\[FILE:(.*?)\]/g;
    matches = content.matchAll(regex);
    for (const match of matches) {
      let fileId = match[1];
      let fileHtml = '<br><a class="p-button" href="' + this.imagesUrl + fileId + '/show" target="_blank">Descargar</a>';
      content = content.replace(match[0], fileHtml);
    }

    return content;
  }

  getFilteredTemplates():Template[] {
    return this.templates.filter((template: Template) => {
      return template.name.toLowerCase().includes(this.templateSearch.toLowerCase()) || this.templateSearch == '';
    });
  }

  //volcamos el contenido de la plantilla en el campo de texto, para enviarlo como mensaje libre dentro de
  //la sesión abierta. Si hay expediente pasamos la plantilla por el parser para que sustituya las
  //variables, igual que al abrir sesión; sin expediente no hay datos con los que sustituirlas.
  //vuelca el contenido de la plantilla y la deja seleccionada, para que el envio salga por Twilio con
  //su ContentSid. Antes solo rellenaba el texto y el mensaje salia como libre: se perdian la plantilla
  //y, con ella, los botones que la plantilla tuviera definidos.
  async selectMessageTemplate(template: Template){
    this.template_id = template.id!;

    if(this.case == null){
      this.message = template.content ?? '';
      return;
    }

    let caseData = await firstValueFrom(this.caseService.get(this.case.id!, { include: ['client'] }));
    this.templatesService.parseHtml(template.id!, caseData).subscribe({
      next: (data: any) => {
        this.message = data.html;
      },
      error: (error: any) => {
        this.messageService.add({ closable: true, severity: 'error', detail: error.error.title });
      }
    });
  }

  //deshace la seleccion para volver a escribir libremente
  clearMessageTemplate(){
    this.template_id = '';
    this.message = '';
  }

  async changeTemplate(event: any){
    if(event != null && event.value !=null){
      let caseData = await firstValueFrom(this.caseService.get(this.case.id!, { include: ['client'] }));

      this.templatesService.parseHtml(event.value, caseData).subscribe({
        next: (data: any) => {
          console.log(data);
          this.message = data.html;
        },
        error: (error: any) => {
          this.messageService.add({ closable: true, severity: 'error', detail: error.error.title });
        }
      });      
    }
  }

  onEnter(event: KeyboardEvent){
    if(event.isComposing || event.keyCode === 229) return; //Enter de confirmacion de IME, no enviar
    if(event.key !== 'Enter' || event.shiftKey) return; //shift+enter inserta salto de linea
    event.preventDefault();
    this.sendMessage();
  }

  async sendMessage(){
      if(!this.message?.trim()) return;

      //si hay plantilla seleccionada la obtenemos
      let template = this.templates.find(m => m.id == this.template_id);

      //sin plantilla es un mensaje libre, y esos solo se pueden enviar con la ventana abierta. El
      //backend tambien lo valida; esto evita que el usuario escriba en balde.
      if(template == null && !this.isSessionOpen){
        this.messageService.add({
          closable: true,
          severity: 'warn',
          summary: 'Ventana cerrada',
          detail: 'No se puede enviar un mensaje libre: han pasado más de 24 horas desde el último mensaje del cliente. Envía una plantilla y espera su respuesta.'
        });
        return;
      }

      let newMessage = new WhatsappMessage();
      delete newMessage.id;
      if(this.case) newMessage.caseId = this.case.id;
      if(template!=null){
        newMessage.contentSid = template.contentSID;
        newMessage.templateId = template.id;
      }
      newMessage.direction = WhatsappMessageDirection.Outbound;
      newMessage.phoneNumber = this.destinationPhoneNumber;
      newMessage.content = this.message.trim();

      try{
        let response = await firstValueFrom(this.whatsappMessageService.Send(newMessage));

        //vaciamos el mensaje solo si salio bien, para no perder lo escrito cuando falla
        this.message = '';
        this.template_id = '';

        //añadimos el mensaje a la lista
        this.addMessage(response);
      }catch(error){
        //el interceptor de errores ya ha mostrado el motivo al usuario. Reevaluamos la ventana por si
        //el backend la considera cerrada y el front no.
        console.error('Error enviando el whatsapp', error);
        this.updateSessionWindow();
      }
    }
  
    async addMessage(message:WhatsappMessage){
      //si el mensaje no esta en ventana, lo añadimos
      if(this.messages.find(m => m.id == message.id)==null) this.messages.push(message);
      this.scrollChatMessages();
    }
  
    //true si la vista esta pegada al final (con margen, porque las imagenes cambian la altura al
    //cargar) o si todavia no hay scroll. Con el contenedor sin renderizar damos por bueno el final.
    isChatAtBottom():boolean{
      let element = this.chatMessages?.nativeElement;
      if(element == null) return true;
      return element.scrollHeight - element.scrollTop - element.clientHeight < 80;
    }

    async scrollChatMessages(){
      setTimeout(() => {
        this.chatMessages.nativeElement.scrollTop = this.chatMessages.nativeElement.scrollHeight;
      }, 300);
    }

    async openTemplateDialog(){
      this.template_id = '';
      this.message = '';
      this.displayTemplateDialog = true;
    }

    async confirmTemplateDialog(){
      this.sendMessage();
      this.displayTemplateDialog = false;
    }

}
